<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.listPlugin')"
        :size="60"
        @close="closeOverSide">
        <div v-if="visible">
            <div class="mb-3 text-right">
                <hts-form-button
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    type="submit"
                    @click="openAddVehiclePlugin">
                    {{ $t('labels.add') }}
                </hts-form-button>
            </div>
            <div class="hts-coordinator-ignore-way__container-body">
                <hts-data-table
                    ref="dataTable"
                    :headers="headerVehiclePlugin"
                    :server="server"
                    :detail-row-auto-height="true"
                    use-selection
                    store-name="vehicleDataTable"
                    store-key="VehiclePlugin" />
            </div>
            <hts-modal
                :show="showPopupDeleteVehiclePlugin"
                :title="$t('labels.deletePlugin')"
                type="confirm"
                :content="$t('labels.deletePluginConfirm')"
                @close="closePopupDeleteVehiclePlugin"
                @confirm="() => actionDeleteVehiclePlugin()" />
            <hts-modal
                :show="showPopupChangeStatusPlugin"
                :title="$t('labels.statusChanges')"
                type="confirm"
                :content="$t('labels.statusChangesContent')"
                @hide="closePopupChangeStatusVehiclePlugin"
                @confirm="() => actionChangesStatusPlugin()" />

            <add-plugin
                ref="addPlugin"
                @refresh-tables="refreshTables()" />
        </div>
    </hts-drawer>
</template>

<script lang="ts" setup>
import { formatTowingCapacity } from '../../ignore/imports/utils'
import { PluginType } from '../imports/form'
import AddPlugin from './add-plugin.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'screen-lg'
  },
  column: {
    type: Number,
    default: 3
  },
  vehicleIsSelectedToView: {
    type: Object
  },
  reload: {
    type: Boolean,
    default: false
  }
})
const codeVehicle = ref('')
const editPlugin: any = ref()
const server = ref({})
const visible = ref(false)
const vehicle = ref<{ [key: string]: any }>({})

const openViewPlugin = (vehicleData: { [key: string]: any }) => {
  visible.value = true
  codeVehicle.value = vehicleData._id
  vehicle.value = vehicleData
  server.value = {
    method: 'post',
    url: '/vehicle/plugin/list/' + vehicleData._id
  }
}

defineExpose({
  openViewPlugin
})

const closeOverSide = () => {
  visible.value = false
}

const dataTable = ref()
const valueStatus = ref()
const dataChangeStatus = ref()
const showPopupChangeStatusPlugin = ref(false)
const showPopupDeleteVehiclePlugin = ref(false)
const vehiclePluginIsSelectedForDelete: { [key: string]: any } = reactive({})

const addPlugin: any = ref()

const openAddVehiclePlugin = () => {
  if (addPlugin.value) {
    addPlugin.value.openAddPlugin(vehicle.value)
  }
}

const handleDeleteVehiclePlugin = (plugin: { [key: string]: any }) => {
  showPopupDeleteVehiclePlugin.value = true
  Object.assign(vehiclePluginIsSelectedForDelete, plugin)
}
const headerVehiclePlugin = [
  {
    field: 'remoocDetail.licensePlate',
    headerName: $t('labels.vehicleModule.pluginName'),
    sortable: true
  },
  {
    field: 'type',
    label: 'type',
    headerName: $t('labels.vehicleModule.pluginType'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      const { type } = params.data
      switch (type) {
      case PluginType.MOOC:
        return $t('labels.supplyList.mooc')
      case PluginType.OIL:
        return $t('labels.supplyList.oil')
      case PluginType.WHEEL:
        return $t('labels.supplyList.wheel')
      case PluginType.GENERATOR:
        return $t('labels.supplyList.generator')
      case PluginType.CONTAINER:
        return $t('labels.supplyList.container')
      case PluginType.CHARGE:
        return $t('labels.supplyList.charge')
      }
    }
  },
  {
    field: 'towingCapacity',
    headerName: $t('labels.vehicleModule.containerCapacity'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.containerType) {
        return null
      }
      if (!params.data.towingCapacity || !params.data.towingCapacity.length) {
        return null
      }
      return formatTowingCapacity(params.data.towingCapacity)
    }
  },
  {
    field: 'description',
    label: 'description',
    headerName: $t('labels.description'),
    sortable: true
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    suppressMenu: true,
    resizable: false,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'Actions',
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'VehiclePlugin',
      items: [
        {
          icon: 'hi-trash-icon',
          label: $t('labels.remove'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.vehicle_plugin_delete),
          click: handleDeleteVehiclePlugin
        }
      ]
    }
  }
]

const refreshTables = () => {
  if (dataTable.value) {
    dataTable.value.refresh()
  }
}

const actionChangesStatusPlugin = () => {
  const api = $url.vehicle.plugin.changeStatus
  const data = {
    id: dataChangeStatus.value.data._id,
    status: valueStatus.value,
    type: dataChangeStatus.value.data.type,
    name: dataChangeStatus.value.data.name
  }
  $api(api, { body: data }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      process.client && $toast().success($t('labels.updateSuccess'))
      refreshTables()
      closePopupChangeStatusVehiclePlugin()
    }
  })
}

const closePopupDeleteVehiclePlugin = () => {
  showPopupDeleteVehiclePlugin.value = false
  Object.assign(vehiclePluginIsSelectedForDelete, {})
}
const closePopupChangeStatusVehiclePlugin = () => {
  showPopupChangeStatusPlugin.value = false
}

const actionDeleteVehiclePlugin = () => {
  const api = $url.vehicle.plugin.removePlugin
  $api(api, { body: { ids: [vehiclePluginIsSelectedForDelete._id], vehicle: codeVehicle.value } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      Object.assign(vehiclePluginIsSelectedForDelete, {})
      refreshTables()
      showPopupDeleteVehiclePlugin.value = false
      process.client && $toast().success(resData.data)
    }
  })
}
</script>
